import { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  CheckCircleOutline,
  ExpandLessRounded,
  ExpandMoreRounded,
  InfoRounded,
} from '@mui/icons-material';
import { Checkbox, Typography, TypographyProps } from '@mui/material';

import Color from 'src/assets/_util.scss';
import {
  ArrowLeftButton,
  ArrowRightButton,
} from 'src/components/core/Button/ArrowButton';
import Button from 'src/components/core/Button/Button';
import { HDivider, VDivider } from 'src/components/core/Divider';
import LoadingSpinner from 'src/components/core/LoadingSpinner/LoadingSpinner';
import { HSpacer } from 'src/components/core/Spacer';
import { HStack, VStack } from 'src/components/core/Stack';
import { View } from 'src/components/core/View';
import type { BannerItem } from 'src/components/Insights/Catalog/HighlightsPageProvider';
import {
  HighlightsPerPage,
  useHighlightsPageContext,
} from 'src/components/Insights/Catalog/HighlightsPageProvider';
import { HighlightsPageSelectionToolbar } from 'src/components/Insights/Catalog/HighlightsPageSelectionToolbar';
import SearchField from 'src/components/Insights/Sidebar/SearchField';
import { AbsoluteFill, TransitionSineOut } from 'src/util/styles';
import FilterPills from '../Filters/FilterPills';

export function HighlightsPageToolbar() {
  const {
    allHighlights,
    currentPage,
    setCurrentPage,
    clearSelection,
    search,
    setSearch,
    t,
    openFilters,
    setOpenFilters,
    resetFilters,
    isLoading,
  } = useHighlightsPageContext();

  const highlightCount = allHighlights.length;
  const pageCount = Math.ceil(highlightCount / HighlightsPerPage);
  const onFirstPage = currentPage === 1;
  const onLastPage = currentPage === pageCount;
  const lowerBound =
    highlightCount === 0 ? 0 : (currentPage - 1) * HighlightsPerPage + 1;
  const upperBound = Math.min(currentPage * HighlightsPerPage, highlightCount);

  const onChangeSearch = (text: string) => {
    setCurrentPage(1);
    setSearch(text);
    clearSelection();
  };

  return (
    <VStack
      sx={{
        borderBottom: `1px solid ${Color.gray300}`,
        position: 'sticky',
        top: 0,
        zIndex: 10,
        background: Color.white,
        padding: '16px 24px',
        width: '100%',
        maxWidth: '1280px',
        alignSelf: 'center',
      }}
    >
      <HStack
        sx={{
          alignItems: 'center',
          gap: '16px',
        }}
      >
        <HStack
          sx={{
            alignItems: 'center',
            gap: '16px',
            flexWrap: 'wrap',
            flexGrow: 1,
          }}
        >
          <Button
            sx={{
              backgroundColor: Color[openFilters ? 'foraPurple' : 'white'],
              color: Color[openFilters ? 'white' : 'foraPurple'],
              borderRadius: '20px',
              border: 'none',
              boxShadow: 'none !important',
            }}
            icon={['far', 'sliders-simple']}
            onClick={() => {
              setOpenFilters((value) => !value);
              resetFilters();
            }}
            data-testid="insights_open_filters_button"
          >
            <Typography fontWeight={openFilters ? 'bold' : 'normal'}>
              {t('insights.filters_title')}
            </Typography>
            <FontAwesomeIcon
              icon={['far', 'close']}
              style={{
                width: openFilters ? '12px' : 0,
                marginLeft: openFilters ? '8px' : 0,
                color: Color[openFilters ? 'white' : 'foraPurple'],
                pointerEvents: 'none',
                overflow: 'hidden',
                ...TransitionSineOut,
              }}
            />
          </Button>
          <SearchField
            labelKey="highlights.search"
            onChange={onChangeSearch}
            initialValue={search}
            sx={{ maxWidth: '300px', margin: 0, width: 'unset' }}
          />
        </HStack>

        <HSpacer grow />
        <Banner />
        <HSpacer grow />

        <HStack
          sx={{
            alignItems: 'center',
            gap: '16px',
            flexWrap: 'wrap',
            flexGrow: 1,
            justifyContent: 'flex-end',
            position: 'relative',
          }}
        >
          <SelectAllTool />
          <VDivider height="36px" />

          <HStack alignItems="center" gap="8px" flexShrink={0}>
            <Typography variant="body1">
              {t('insights.showing_range_description', {
                lowerBound,
                upperBound,
                count: highlightCount,
              })}
            </Typography>
            <HSpacer width="2px" />

            <ArrowLeftButton
              size={28}
              disabled={onFirstPage}
              onClick={() => setCurrentPage(currentPage - 1)}
              aria-label={t('common.previous_page')}
            />
            <ArrowRightButton
              size={28}
              disabled={onLastPage || highlightCount === 0}
              onClick={() => setCurrentPage(currentPage + 1)}
              aria-label={t('common.next_page')}
            />
          </HStack>

          <View
            sx={{
              ...AbsoluteFill,
              background: Color.white,
              transition: 'all 300ms ease-out',
              opacity: isLoading ? 1 : 0,
              zIndex: 1,
              textAlign: 'center',
              pointerEvents: isLoading ? 'auto' : 'none',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <LoadingSpinner active size="md" />
          </View>
        </HStack>
      </HStack>

      <FilterPills />

      <HighlightsPageSelectionToolbar />
    </VStack>
  );
}

function Banner() {
  const AnimDuration = 300; // ms
  const { banners, hideBanner, t, taggingProgress } =
    useHighlightsPageContext();
  const { unsuccessfulCount } = taggingProgress;
  const [expanded, setExpanded] = useState(false);

  // stores a local copy of the most-recently added banner
  // so that we can animate the banner away with its content still visible.
  const [mainBanner, setMainBanner] = useState<BannerItem>();

  const bannerCount = banners.length;
  const visible = bannerCount > 0;
  const canExpand = !!mainBanner?.details || bannerCount > 1;
  const ExpandIcon = expanded ? ExpandLessRounded : ExpandMoreRounded;

  const progress = useMemo(() => {
    let progress = 0;
    let progressSum = 0;
    banners.forEach((b) => {
      if (b.progress) {
        progress += b.progress;
        progressSum += 100;
      }
    });
    return progressSum
      ? (progress / progressSum) * 100
      : mainBanner?.progress ?? 0;
  }, [banners, mainBanner]);

  const onClick = () => {
    setExpanded(!expanded);
  };

  useEffect(() => {
    // store the most-recently added banner locally.
    // we use this to keep the banner content visible while it animates away.
    setMainBanner((prev) => banners.at(0) ?? prev);

    // set up auto-hide if needed
    banners.forEach((b) => {
      if (!b.progress) setTimeout(() => hideBanner(b.id), b.duration ?? 4000);
    });

    // collapse details if needed
    if (banners.length === 1 && !banners[0].details) setExpanded(false);
    if (banners.length === 0)
      setTimeout(() => setExpanded(false), AnimDuration);
  }, [banners, hideBanner]);

  return (
    <View
      sx={{
        position: 'absolute',
        top: '12px',
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        pointerEvents: 'none',
      }}
    >
      <VStack
        sx={{
          borderRadius: '20px',
          backgroundColor: Color.foraPurple,
          color: Color.white,
          p: '12px 16px',
          textWrap: 'none',
          boxShadow: expanded ? '0 0 8px rgba(0, 0, 0, 0.2)' : 'none',
          transitionProperty: 'opacity, filter, transform, box-shadow',
          transitionDuration: `${AnimDuration}ms`,
          transitionTimingFunction: 'ease-out',
          opacity: visible ? 1 : 0,
          filter: visible ? 'none' : 'blur(2px)',
          transform: visible ? '' : 'translate(0, 3px) scale(0.95)',
          pointerEvents: visible ? 'auto' : 'none',
          userSelect: 'none',
          cursor: canExpand ? 'pointer' : 'default',
          zIndex: 50,
        }}
      >
        <HStack
          sx={{
            alignItems: 'center',
            marginBottom: expanded ? '12px' : 0,
          }}
          onClick={onClick}
        >
          {(!!progress || bannerCount > 1) && (
            <View
              sx={{
                position: 'relative',
                margin: '0 8px 0 0',
                width: '24px',
                aspectRatio: '1',
              }}
            >
              {bannerCount > 1 && (
                <Typography
                  sx={{
                    fontSize: 16,
                    fontWeight: 600,
                    color: Color.foraPurpleDark,
                    top: 0,
                    left: 0,
                    borderRadius: '100%',
                    background: Color.white,
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  {bannerCount}
                </Typography>
              )}
              {progress === 100 && unsuccessfulCount ? (
                <InfoRounded sx={{ color: Color.insightsPending }} />
              ) : progress === 100 && !unsuccessfulCount ? (
                <CheckCircleOutline sx={{ color: Color.tealLight }} />
              ) : progress > 0 ? (
                <div style={{ paddingLeft: '4px' }}>
                  <LoadingSpinner active size="sm" thick />
                </div>
              ) : null}
            </View>
          )}
          <VStack>
            <BannerMessage sx={{ margin: '0 4px', fontWeight: '700' }}>
              {bannerCount > 1
                ? t('insights.tasksInProgress')
                : mainBanner?.message ?? ''}
            </BannerMessage>
            <TaggingBannerProgressBar />
          </VStack>

          {canExpand && <ExpandIcon sx={{ margin: '0 0 0 4px' }} />}
        </HStack>

        {expanded &&
          (bannerCount > 1 ? (
            <VStack sx={{ margin: '12px 4px 4px', gap: '16px' }}>
              {banners?.map((banner, index) => (
                <VStack key={banner.id}>
                  <BannerMessage>{banner.message}</BannerMessage>
                  {banner.details}

                  {index !== bannerCount - 1 && (
                    <HDivider
                      color={Color.foraPurpleLight}
                      sx={{ marginTop: '16px' }}
                    />
                  )}
                </VStack>
              ))}
            </VStack>
          ) : (
            mainBanner?.details ?? null
          ))}
      </VStack>
    </View>
  );
}

function BannerMessage({ style, ...props }: TypographyProps) {
  return (
    <Typography
      variant="body1"
      sx={{ fontWeight: '600', flex: 1, ...style }}
      {...props}
    />
  );
}

function TaggingBannerProgressBar() {
  const { taggingProgress, t } = useHighlightsPageContext();
  const { percent } = taggingProgress;
  const pending = taggingProgress?.pendingCount ?? 0;
  const unsuccessful = taggingProgress?.unsuccessfulCount ?? 0;
  const successful = taggingProgress?.succeededCount ?? 0;
  const total = pending + unsuccessful + successful || 1;

  const minBarWidth = 8; // px

  const calcWidth = (count: number) => {
    const percent = (count / total) * 100;
    return `max(${percent}%, ${minBarWidth}px)`;
  };

  const s = {
    barSegment: {
      border: `2px solid ${Color.foraPurple}`,
      borderRadius: '12px',
      transition: 'width 0.3s',
    },
  };

  return (
    <HStack
      aria-label={t('insights.taggingProgressBar')}
      sx={{
        height: '8px',
        borderRadius: '12px',
        overflow: 'hidden',
        margin: '4px 0',
        background: Color.foraPurple,
      }}
    >
      <div
        aria-label={t('insights.taggingProgressBarSuccess')}
        style={{
          background: Color.insightsSuccess,
          width: successful ? calcWidth(successful) : 0,
          display: successful ? 'block' : 'none',
          ...s.barSegment,
        }}
        title={t('insights.taggingBarSuccess', { count: successful })}
      />
      <div
        aria-label={t('insights.taggingProgressBarUnsuccessful')}
        style={{
          background: Color.insightsFailure,
          width: unsuccessful ? calcWidth(unsuccessful) : 0,
          display: unsuccessful ? 'block' : 'none',
          ...s.barSegment,
        }}
        title={t('insights.taggingBarUnsuccessful', { count: unsuccessful })}
      />
      <div
        aria-label={t('insights.taggingProgressBarPending')}
        style={{
          background: Color.insightsPending,
          width: percent === 100 ? 0 : calcWidth(pending),
          ...s.barSegment,
        }}
        title={t('insights.taggingBarPending', { count: pending })}
      />
    </HStack>
  );
}

function SelectAllTool() {
  const {
    visibleHighlights,
    selectedHighlightIds,
    setSelectedHighlightIds,
    t,
  } = useHighlightsPageContext();

  const disabled = !visibleHighlights || visibleHighlights.length === 0;
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (disabled) {
      setChecked(false);
    } else {
      setChecked(
        visibleHighlights.every((h) => selectedHighlightIds.has(h.id))
      );
    }
  }, [selectedHighlightIds, visibleHighlights, disabled]);

  const onChange = (enabled: boolean) => {
    if (disabled) return;
    setSelectedHighlightIds((prevSelected) => {
      const nowSelectedHighlights = new Set(prevSelected);
      visibleHighlights.forEach((h) =>
        nowSelectedHighlights[enabled ? 'add' : 'delete'](h.id)
      );
      return nowSelectedHighlights;
    });
    setChecked(enabled);
  };

  return (
    <HStack sx={{ alignItems: 'center', flexShrink: 0, position: 'relative' }}>
      <Checkbox
        size="small"
        sx={{ color: Color[checked ? 'foraPurple' : 'gray650'] }}
        onChange={(e, checked) => onChange(checked)}
        checked={checked}
        aria-label={t('common.select_all_on_page')}
        data-testid="select-all-checkbox"
        disabled={disabled}
      />
      <Typography
        variant="body1"
        onClick={() => onChange(!checked)}
        sx={{
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
      >
        {t('common.select_all_on_page')}
      </Typography>
    </HStack>
  );
}
